import React, { useMemo } from 'react';
import Select from 'react-select';

/** Sized to match the plain .form-control inputs sitting beside it. */
const styles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: '0.875rem',
    borderColor: state.isFocused ? '#80bdff' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
    '&:hover': { borderColor: '#80bdff' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  placeholder: (base) => ({ ...base, fontSize: '0.875rem', color: '#868e96' }),
  singleValue: (base) => ({ ...base, fontSize: '0.875rem' }),
  indicatorsContainer: (base) => ({ ...base, height: 36 }),
  menu: (base) => ({ ...base, fontSize: '0.875rem', zIndex: 30 }),
  menuPortal: (base) => ({ ...base, zIndex: 30000 }),
};

/**
 * A type-to-filter dropdown for the long lists in this module — job titles,
 * departments and the employee pickers, which run to hundreds of rows.
 *
 * The menu is portalled to document.body so it is not clipped by the dialog's
 * scrolling body.
 */
const SearchSelect = ({
  value,
  onChange,
  options,
  placeholder = 'Search…',
  required = false,
  isClearable = true,
  isDisabled = false,
}) => {
  // Values arrive from form state as strings; option values may be numbers.
  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value ?? '')) ?? null,
    [options, value]
  );

  return (
    <Select
      value={selected}
      onChange={(option) => onChange(option ? option.value : '')}
      options={options}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      styles={styles}
      menuPortalTarget={typeof document === 'undefined' ? null : document.body}
      menuPosition="fixed"
      classNames={required ? { control: () => 'required-select' } : undefined}
      noOptionsMessage={() => 'No matches'}
    />
  );
};

export default SearchSelect;

/** "Firstname Lastname (CODE)" options keyed by user_id. */
export const employeeOptions = (employees) =>
  employees.map((emp) => ({
    value: emp.user_id,
    label: `${[emp.first_name, emp.last_name].filter(Boolean).join(' ')} (${emp.employee_id})`,
  }));

export const jobOptions = (jobs) =>
  jobs.map((job) => ({ value: job.job_id, label: job.job_title }));

export const departmentOptions = (departments) =>
  departments.map((dept) => ({ value: dept.department_id, label: dept.department_name }));

/**
 * "Referred by" carries two sentinels the old app defined alongside the real
 * employees: 500 = not referred, 600 = someone outside the company.
 */
export const NOT_REFERRED = '500';
export const REFERRED_OTHERS = '600';

export const referrerOptions = (employees) => [
  { value: NOT_REFERRED, label: 'Not Referred' },
  { value: REFERRED_OTHERS, label: 'Others' },
  ...employeeOptions(employees),
];
